/** @jsxImportSource @emotion/react */
import { Route, Routes, useMatch } from 'react-router-dom';
import styled from '@emotion/styled';
import NotFoundPage from '@page/error/not-found';
import * as color from '@constant/color';
import * as Sentry from '@sentry/react';
import ErrorPage from '@page/error';
import React, { useEffect } from 'react';
import { useTypedDispatch, useTypedSelector } from '@hook/common/useStore';
import OAuthCallback from '@page/auth/oauth-callback';
import TermPage from '@page/auth/term-page';
import Signout from '@page/auth/signout';
import Signup from '@page/auth/signup';
import Signin from '@page/auth/signin';
import AuthCode from '@page/auth/auth-code';
import MyPage from '@page/auth/my-page';
import FindId from 'page/auth/find-id';
import { actions as errorActions } from '@store/slice/error-slice';
import { actions as modalActions } from '@store/slice/modal-slice';
import { actions as gridgioModalActions } from '@store/slice/gridgio/modal-slice';
import { actions as navigationActions } from '@store/slice/navigation-slice';
import FindPassword from '@page/auth/find-password';
import IyagigoApp from '@app/iyagigo-app';
import MoashelfApp from '@app/moashelf-app';
import GridgioApp from '@app/gridgio-app';
import AdminApp from '@app/admin-app';
import { SERVICE } from '@config/environment';
import { useWebVersionCheck, useApplicationVersionCheck } from '@hook/common/useVersion';
import WebRefreshModal from '@component/common/modal/web-refresh-modal';
import ApplicationUpdateModal from '@component/common/modal/application-update-modal';
import { AUTH_PATH } from '@constant/auth/route-path';
import { useApiMutation } from '@hook/common/useApi';
import axios from '@config/axios-config';
import { EService } from '@datatype/type';

// useMutation은 suspense fallback 반영 X
// useQuery는 suspense fallback 반영 O

const MobileWrapper = styled.div`
  position: fixed;
  top: 0;
  bottom: 0;
  width: 100vw;
  height: 100vh;
  background-color: ${color.BACKGROUND};
  overflow: hidden;
  padding-top: var(--sat);
  padding-bottom: var(--sab);
`;

// IOS -> O & Android -> X
// padding-top: env(safe-area-inset-top);
// padding-bottom: env(safe-area-inset-bottom);

const WebWrapper = styled.div`
  position: relative;
  width: 100vw;
  height: 100vh;
  background-color: ${color.BACKGROUND};
  overflow: hidden;
`;

const ErrorTestButtonContainer = styled.ul`
  position: fixed;
  bottom: 20px;
  right: 20px;
  z-index: 100;
  display: flex;
  flex-flow: column nowrap;
  gap: 10px;
`;

const RootApp = () => {
  const dispatch = useTypedDispatch();
  const hostname = window.location.hostname;

  const isSignupPage = useTypedSelector((state) => state.rootReducer?.authReducer?.isSignupPage) ?? false;
  const isSigninPage = useTypedSelector((state) => state.rootReducer?.authReducer?.isSigninPage) ?? false;
  const isSignoutPage = useTypedSelector((state) => state.rootReducer?.authReducer?.isSignoutPage) ?? false;
  const isFindIdPage = useTypedSelector((state) => state.rootReducer?.authReducer?.isFindIdPage) ?? false;
  const isFindPasswordPage = useTypedSelector((state) => state.rootReducer?.authReducer?.isFindPasswordPage) ?? false;
  const isAuthCodePage = useTypedSelector((state) => state.rootReducer?.authReducer?.isAuthCodePage) ?? false;
  const isMyPage = useTypedSelector((state) => state.rootReducer?.authReducer?.isMyPage) ?? false;

  // 조건부 hook 호출 금지 - 모두 호출 후 선택
  const msMatch = useMatch('/ms/:page');
  const igMatch = useMatch('/ig/:page');
  const giMatch = useMatch('/gi/:page/*');
  const match =
    SERVICE === EService.IYAGIGO
      ? igMatch
      : SERVICE === EService.MOASHELF
        ? msMatch
        : SERVICE === EService.GRIDGIO
          ? giMatch
          : null;
  const page = match?.params.page;

  // ###################################
  // initialize
  // - 새로고침되면 실행
  // - '/' 경로에서 실행
  // ###################################
  useEffect(() => {
    dispatch(modalActions.initialize());
    if (SERVICE === EService.GRIDGIO) dispatch(gridgioModalActions.initialize());
    dispatch(errorActions.initialize());
    if (page && (SERVICE === EService.IYAGIGO || SERVICE === EService.MOASHELF)) {
      dispatch(navigationActions.syncPageAndMenu({ service: SERVICE, page }));
    }
  }, [page]);

  // 웹(새로고침) / 앱(스토어 업데이트) 버전 체크 — 반환 필드명이 겹치므로 접두사로 구분한다
  const {
    needRefresh: needWebRefresh,
    releaseVersion: webReleaseVersion,
    releaseNote: webReleaseNote,
    handleRefresh: handleWebRefresh,
  } = useWebVersionCheck();
  const {
    needUpdate: needApplicationUpdate,
    isForce: isApplicationUpdateForce,
    releaseNote: applicationReleaseNote,
    handleDismiss: handleApplicationUpdateDismiss,
  } = useApplicationVersionCheck();

  // ############### error test ###############
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { mutate: handleApiErrorTest } = useApiMutation(async () => {
    // 실제 axios 요청을 보내서 interceptor가 동작하도록 함
    await axios.get('/api/v1/error-test');
  });

  const handleCodeErrorTest = () => {
    throw new Error('test error');
  };

  return (
    <MobileWrapper>
      <Sentry.ErrorBoundary
        fallback={({ error, resetError }) => <ErrorPage error={error as Error} resetErrorBoundary={resetError} />}
        onReset={() => {
          // reset the state of your app so the error doesn't happen again
        }}>
        {/* {process.env.NODE_ENV !== 'production' && (
          <ErrorTestButtonContainer>
            <TextButton
              text='API Error'
              width='100px'
              size='medium'
              handleClick={() => handleApiErrorTest(undefined)}
            />
            <TextButton text='Code Error' width='100px' size='medium' handleClick={handleCodeErrorTest} />
          </ErrorTestButtonContainer>
        )} */}
        {/* 앱 업데이트가 웹 새로고침보다 우선한다 — 앱이 구버전이면 웹을 새로고침해도 해결되지 않는다 */}
        {needApplicationUpdate && (
          <ApplicationUpdateModal
            isForce={isApplicationUpdateForce}
            releaseNote={applicationReleaseNote}
            handleDismiss={handleApplicationUpdateDismiss}
          />
        )}
        {!needApplicationUpdate && needWebRefresh && (
          <WebRefreshModal
            releaseVersion={webReleaseVersion}
            releaseNote={webReleaseNote}
            handleRefresh={handleWebRefresh}
          />
        )}
        {isSignupPage && <Signup />}
        {isSigninPage && <Signin />}
        {isSignoutPage && <Signout />}
        {isFindIdPage && <FindId />}
        {isFindPasswordPage && <FindPassword />}
        {isAuthCodePage && <AuthCode />}
        {isMyPage && <MyPage />}
        <Routes>
          {/* term */}
          <Route path={AUTH_PATH.SERVICE_TERM_PATH} element={<TermPage mode={'service-term'} />} />
          <Route path={AUTH_PATH.PRIVATE_TERM_PATH} element={<TermPage mode={'private-term'} />} />
          <Route path={AUTH_PATH.ACCOUNT_TERM_PATH} element={<TermPage mode={'account-term'} />} />
          {/* auth */}
          <Route path={AUTH_PATH.OAUTH_CALLBACK_PATH} element={<OAuthCallback />} />
          {/* moashelf */}
          {SERVICE === EService.MOASHELF && <Route path='/*' element={<MoashelfApp />} />}
          {/* gridgio */}
          {SERVICE === EService.GRIDGIO && <Route path='/*' element={<GridgioApp />} />}
          {/* iyagigo */}
          {SERVICE === EService.IYAGIGO && <Route path='/*' element={<IyagigoApp />} />}
          {/* admin */}
          {SERVICE === EService.ADMIN && <Route path='/*' element={<AdminApp />} />}
          {/* not found page */}
          <Route path='*' element={<NotFoundPage />} />
        </Routes>
      </Sentry.ErrorBoundary>
    </MobileWrapper>
  );
};

export default RootApp;
